import { Link, useNavigate } from '@tanstack/react-router';
import type { ActivityDetail } from '@painpal/contracts';
import { Trash2 } from 'lucide-react';
import { useDeleteActivity, useRetryActivity } from '../../api/mutations';
import { useActivity } from '../../api/queries';
import { ConfirmButton } from '../../components/ConfirmButton';
import { Eyebrow } from '../../components/Eyebrow';
import { Headline } from '../../components/Headline';
import { ErrorPanel, LoadingBlock } from '../../components/StateViews';
import { ApiRequestError, errorMessage } from '../../lib/api';
import { splitHeadline } from '../../lib/format';
import { detailEyebrow } from './detailModel';
import { HeroRow } from './HeroRow';
import { HrChartCard } from './HrChartCard';
import { PowerCurveCard } from './PowerCurveCard';
import { detailLayout, gpsOf, layoutNoun, streamsTitle, type DetailLayout } from './runDetailModel';
import { SplitsCard } from './SplitsCard';
import { StreamTracks } from './StreamTracksPanel';
import { useActivityStreams, useStreams } from './streams';
import { ZonesCard } from './ZonesCard';
import { showsAnalysis } from './zonesModel';
import styles from './detail.module.css';

function StreamsPanel({ activity, url, layout }: { activity: ActivityDetail; url: string; layout: DetailLayout }) {
  const streams = useStreams(activity.id, url);
  return (
    <section className={styles.streams} aria-labelledby="streams-title">
      <h2 id="streams-title" className={styles.streamsTitle}>{streamsTitle(layout)}</h2>
      {streams.isPending && <LoadingBlock label={`LOADING ${layoutNoun(layout)} DATA`} />}
      {streams.isError && <ErrorPanel error={streams.error} onRetry={() => void streams.refetch()} title={`${layoutNoun(layout)} DATA UNAVAILABLE`} />}
      {streams.isSuccess && <StreamTracks streams={streams.data} activity={activity} />}
    </section>
  );
}

function StatusBanner({ activity, onRetry, retrying }: { activity: ActivityDetail; onRetry: () => void; retrying: boolean }) {
  if (activity.status === 'pending') {
    return <p className={`alert-amber ${styles.banner}`} data-tone="amber" role="status">PROCESSING… THIS PAGE UPDATES BY ITSELF</p>;
  }
  if (activity.status === 'failed') {
    return (
      <div className={`alert-amber ${styles.banner}`} data-tone="hot" role="alert">
        PROCESSING FAILED · {activity.error ?? 'UNKNOWN ERROR'}
        <button type="button" className="btn btn-outline" disabled={retrying} onClick={onRetry}>RETRY ↻</button>
      </div>
    );
  }
  return null;
}

// Each analysis card checks its own data; the slot left of ZONES holds the power curve, km splits or HR chart.
function AnalysisSlot({ activity, layout }: { activity: ActivityDetail; layout: DetailLayout }) {
  const url = activity.streamsUrl;
  if (layout === 'ride') return activity.avgPower === null ? null : <PowerCurveCard activityId={activity.id} />;
  if (url === null) return null;
  if (layout === 'run') return <SplitsCard activity={activity} url={url} />;
  return activity.avgHr === null ? null : <HrChartCard activity={activity} url={url} />;
}

function DetailView({ activity }: { activity: ActivityDetail }) {
  const navigate = useNavigate();
  const remove = useDeleteActivity();
  const retry = useRetryActivity();
  const streams = useActivityStreams(activity.id, activity.streamsUrl);
  const gps = gpsOf(activity.streamsUrl, streams.data);
  const layout = detailLayout(activity);
  const analysis = showsAnalysis(activity);
  const [first, second] = splitHeadline(activity.name);
  const onDelete = (): void => remove.mutate(activity.id, { onSuccess: () => void navigate({ to: '/activities' }) });
  return (
    <>
      <header className={styles.header}>
        <Eyebrow>{detailEyebrow(activity, gps)}</Eyebrow>
        <Headline first={first} second={second} />
        {remove.isError && <p role="alert" className={`alert-amber ${styles.banner}`} data-tone="hot">{errorMessage(remove.error)}</p>}
        <StatusBanner activity={activity} onRetry={() => retry.mutate(activity.id)} retrying={retry.isPending} />
      </header>
      <div className={styles.actions} data-part="actions">
        {activity.stravaUrl !== null && (
          <a href={activity.stravaUrl} target="_blank" rel="noopener" className={styles.stravaLink}>VIEW ON STRAVA ↗</a>
        )}
        <ConfirmButton
          label={<><Trash2 size={13} aria-hidden="true" /> DELETE</>} confirmLabel="YES, DELETE RIDE"
          prompt="DELETE THIS ACTIVITY?" busy={remove.isPending} onConfirm={onDelete}
        />
      </div>
      <HeroRow activity={activity} gps={gps} />
      {analysis && <div className={styles.curveArea}><AnalysisSlot activity={activity} layout={layout} /></div>}
      {analysis && <div className={styles.zonesArea}><ZonesCard activity={activity} /></div>}
      {activity.streamsUrl !== null && layout !== 'hr' && <StreamsPanel activity={activity} url={activity.streamsUrl} layout={layout} />}
    </>
  );
}

export function ActivityDetailPage({ activityId }: { activityId: string }) {
  const detail = useActivity(activityId);
  const notFound = detail.error instanceof ApiRequestError && detail.error.status === 404;
  return (
    <div className={styles.content}>
      <Link to="/activities" className={styles.back}>← ALL ACTIVITIES</Link>
      {detail.isPending && <div className={styles.state}><LoadingBlock label="LOADING ACTIVITY" height={420} /></div>}
      {detail.isError && (
        <div className={styles.state}>
          <ErrorPanel error={detail.error} onRetry={() => void detail.refetch()} title={notFound ? 'ACTIVITY NOT FOUND' : 'COULDN’T LOAD THIS ACTIVITY'} />
        </div>
      )}
      {detail.isSuccess && <DetailView activity={detail.data} />}
    </div>
  );
}
